import { isCookiebotAnalyticsConsentGiven } from "@/utils/analytics/cookiebot";

export type Ga4IdentifierPair = {
  ga_client_id: string;
  /**
   * Nullable once the observed GA session ages out of its freshness window.
   * The client id is stable and must survive that expiry on its own.
   */
  ga_session_id: string | null;
};

export type Ga4Identifiers = Ga4IdentifierPair & {
  ga_session_observed_at: string;
};

type Ga4IdentifierWindow = Pick<Window, "Cookiebot" | "gtag"> & {
  document?: { cookie?: string };
};
type Ga4IdentifierLifecycleWindow = Ga4IdentifierWindow &
  Pick<Window, "addEventListener" | "removeEventListener">;

export type Ga4IdentifierCache = {
  clear: () => void;
  destroy: () => void;
  getFreshIdentifiers: () => Ga4Identifiers | null;
  refresh: () => Promise<Ga4Identifiers | null>;
  start: () => void;
};

export const GA4_IDENTIFIER_MAX_ATTEMPTS = 8;
export const GA4_IDENTIFIER_CALLBACK_TIMEOUT_MS = 125;
export const GA4_IDENTIFIER_RETRY_DELAY_MS = 75;
/** Freshness window for the volatile GA4 session id. */
export const GA4_IDENTIFIER_MAX_AGE_MS = 4 * 60 * 1000;
/**
 * The GA client id is stable for the lifetime of the browser/property pair, so
 * it uses a much longer window than the session id. Long checkout journeys
 * must keep it even when no fresh session id is observable.
 */
export const GA4_CLIENT_ID_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const normalizeIdentifier = (value: unknown): string | null => {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const identifier = String(value).trim();
  return identifier ? identifier : null;
};

const normalizeGaSessionId = (value: unknown): string | null => {
  const identifier = normalizeIdentifier(value);
  return identifier && /^[1-9]\d{0,19}$/.test(identifier) ? identifier : null;
};

/* ------------------------------------------------------------------ *
 * Cookie join.
 *
 * Production loads GA4 through GTM. The `gtag` function on window is the
 * GTM shim (`function gtag(){dataLayer.push(arguments);}`), so a
 * `gtag("get", …)` command never reaches gtag.js and its callback never
 * fires — the retry loop below would time out on every attempt and the
 * tracking payload kept `ga_client_id`/`ga_session_observed_at` null even
 * though the visitor's `_ga` cookie was right there. Reading Google's own
 * first-party cookies makes the join deterministic and consent-gated:
 *   _ga            → GA1.<n>.<client_id>.<timestamp>
 *   _ga_<G-XXXX…>  → GS1.1.<session_id>.<last_benchmark_ms>.<hits>.<n>
 */

const GA_COOKIE_NAME = "_ga";
const GA_SESSION_COOKIE_PREFIX = "_ga_";
const GA_CLIENT_COOKIE_PATTERN = /^GA\d\.\d+\.(.+)$/;

/** `<session>.<benchmarkMs>` of a GA4 session cookie, null when malformed. */
const parseGaSessionCookie = (
  value: string,
): { sessionId: string; benchmarkAtMs: number } | null => {
  const parts = decodeURIComponent(value).split(".");
  const sessionId = normalizeGaSessionId(parts[2]);
  const benchmarkAtMs = Number(parts[3]);

  if (!sessionId || !Number.isFinite(benchmarkAtMs)) {
    return null;
  }

  return { sessionId, benchmarkAtMs };
};

/** What the cookies alone could recover; either field may be missing. */
export type Ga4CookieJoinResult = {
  ga_client_id: string | null;
  ga_session_id: string | null;
};

/**
 * Reads the GA4 client/session ids from the browser's Google Analytics
 * cookies without any dependency on the gtag API. Returns null when neither
 * id can be recovered.
 */
export const readGa4IdentifiersFromCookies = ({
  cookieString,
  measurementId,
}: {
  cookieString: string;
  measurementId?: string;
}): Ga4CookieJoinResult | null => {
  if (!cookieString) {
    return null;
  }

  const cookies = new Map<string, string>();
  cookieString.split(";").forEach((pair) => {
    const separatorIndex = pair.indexOf("=");
    if (separatorIndex <= 0) return;
    const name = pair.slice(0, separatorIndex).trim();
    if (!name || cookies.has(name)) return;
    cookies.set(name, pair.slice(separatorIndex + 1).trim());
  });

  let gaClientId: string | null = null;
  const gaCookieValue = cookies.get(GA_COOKIE_NAME);
  if (gaCookieValue) {
    gaClientId = normalizeIdentifier(
      GA_CLIENT_COOKIE_PATTERN.exec(gaCookieValue)?.[1],
    );
  }

  // Prefer the session cookie of the configured property; otherwise take the
  // most recently benchmarked GA4 session cookie so multi-property setups
  // still yield the active visit instead of an arbitrary one.
  const suffix =
    measurementId && /^G-[A-Z0-9]+$/.test(measurementId)
      ? measurementId.slice(2)
      : null;
  const preferredCookie = suffix
    ? cookies.get(`${GA_SESSION_COOKIE_PREFIX}${suffix}`) ??
      cookies.get(`${GA_SESSION_COOKIE_PREFIX}${suffix.toLowerCase()}`)
    : undefined;

  let gaSessionCookie: { sessionId: string; benchmarkAtMs: number } | null =
    preferredCookie ? parseGaSessionCookie(preferredCookie) : null;

  if (!gaSessionCookie) {
    const scanned = Array.from(cookies.entries())
      .flatMap(([name, value]) => {
        if (!name.startsWith(GA_SESSION_COOKIE_PREFIX)) return [];
        const parsed = parseGaSessionCookie(value);
        return parsed ? [{ parsed }] : [];
      })
      .sort((a, b) => b.parsed.benchmarkAtMs - a.parsed.benchmarkAtMs);

    gaSessionCookie = scanned[0]?.parsed ?? null;
  }

  if (!gaClientId && !gaSessionCookie) {
    return null;
  }

  // Partial pairs are valid: the stable client id alone keeps the attribution
  // anchor alive, and the async gtag path can still complete the session id.
  return {
    ga_client_id: gaClientId,
    ga_session_id: gaSessionCookie?.sessionId ?? null,
  };
};

const wait = (delayMs: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const getGtagField = (
  targetWindow: Ga4IdentifierWindow,
  measurementId: string,
  fieldName: "client_id" | "session_id",
  callbackTimeoutMs: number,
): Promise<string | null> =>
  new Promise((resolve) => {
    if (typeof targetWindow.gtag !== "function") {
      resolve(null);
      return;
    }

    let settled = false;
    const settle = (value: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(
        fieldName === "session_id"
          ? normalizeGaSessionId(value)
          : normalizeIdentifier(value),
      );
    };
    const timeout = setTimeout(() => settle(null), callbackTimeoutMs);

    try {
      if (!isCookiebotAnalyticsConsentGiven(targetWindow)) {
        settle(null);
        return;
      }

      targetWindow.gtag("get", measurementId, fieldName, settle);
    } catch {
      settle(null);
    }
  });

export const getGoogleAnalyticsIdentifiers = async ({
  targetWindow = typeof window === "undefined" ? null : window,
  measurementId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TAG_ID?.trim() ?? "",
  maxAttempts = GA4_IDENTIFIER_MAX_ATTEMPTS,
  callbackTimeoutMs = GA4_IDENTIFIER_CALLBACK_TIMEOUT_MS,
  retryDelayMs = GA4_IDENTIFIER_RETRY_DELAY_MS,
}: {
  targetWindow?: Ga4IdentifierWindow | null;
  measurementId?: string;
  maxAttempts?: number;
  callbackTimeoutMs?: number;
  retryDelayMs?: number;
} = {}): Promise<Ga4IdentifierPair | null> => {
  if (
    !targetWindow ||
    !/^G-[A-Z0-9]+$/.test(measurementId) ||
    !isCookiebotAnalyticsConsentGiven(targetWindow)
  ) {
    return null;
  }

  // The cookie join answers synchronously and does not depend on gtag at all
  // (see readGa4IdentifiersFromCookies). When it already yields the full pair,
  // waiting out the bounded gtag retries would be pure latency.
  let cookiePair: Ga4CookieJoinResult | null =
    readGa4IdentifiersFromCookies({
      cookieString: targetWindow.document?.cookie ?? "",
      measurementId,
    }) ?? null;

  const attempts = Math.max(1, maxAttempts);

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (!isCookiebotAnalyticsConsentGiven(targetWindow)) {
      return null;
    }

    // GA may create its cookies while the bounded gtag retries are running.
    // Re-read on every attempt so the deterministic fallback observes them.
    cookiePair =
      readGa4IdentifiersFromCookies({
        cookieString: targetWindow.document?.cookie ?? "",
        measurementId,
      }) ?? cookiePair;

    if (cookiePair?.ga_client_id && cookiePair.ga_session_id) {
      return {
        ga_client_id: cookiePair.ga_client_id,
        ga_session_id: cookiePair.ga_session_id,
      };
    }

    const [gaClientId, gaSessionId] = await Promise.all([
      getGtagField(targetWindow, measurementId, "client_id", callbackTimeoutMs),
      getGtagField(
        targetWindow,
        measurementId,
        "session_id",
        callbackTimeoutMs,
      ),
    ]);

    if (!isCookiebotAnalyticsConsentGiven(targetWindow)) {
      return null;
    }

    // A partial pair is completed from either source — a cookie client id
    // with a gtag session id (or vice versa) still describes this visit.
    const mergedClientId = gaClientId ?? cookiePair?.ga_client_id ?? null;
    const mergedSessionId = gaSessionId ?? cookiePair?.ga_session_id ?? null;

    if (mergedClientId && mergedSessionId) {
      return {
        ga_client_id: mergedClientId,
        ga_session_id: mergedSessionId,
      };
    }

    if (attempt + 1 < attempts) {
      await wait(retryDelayMs);
    }
  }

  if (!cookiePair?.ga_client_id) {
    return null;
  }

  return {
    ga_client_id: cookiePair.ga_client_id,
    ga_session_id: cookiePair.ga_session_id,
  };
};

export const createGa4IdentifierCache = ({
  targetWindow = typeof window === "undefined" ? null : window,
  onChange,
  lookup = () => getGoogleAnalyticsIdentifiers({ targetWindow }),
  // Synchronous fallback for paths that cannot await the async lookup (exit
  // beacons, order submits): Google's own first-party cookies.
  cookieLookup = (): Ga4CookieJoinResult | null =>
    readGa4IdentifiersFromCookies({
      cookieString: targetWindow?.document?.cookie ?? "",
      measurementId:
        process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TAG_ID?.trim() ?? "",
    }),
  now = Date.now,
}: {
  targetWindow?: Ga4IdentifierLifecycleWindow | null;
  onChange: (identifiers: Ga4Identifiers | null) => void;
  lookup?: () => Promise<Ga4IdentifierPair | null>;
  cookieLookup?: () => Ga4CookieJoinResult | null;
  now?: () => number;
}): Ga4IdentifierCache => {
  let identifiers: Ga4Identifiers | null = null;
  let clientIdCapturedAt: number | null = null;
  let sessionIdCapturedAt: number | null = null;
  let consentGeneration = 0;
  let pendingGeneration: number | null = null;
  let pendingLookup: Promise<Ga4Identifiers | null> | null = null;

  const clear = (): void => {
    identifiers = null;
    clientIdCapturedAt = null;
    sessionIdCapturedAt = null;
    onChange(null);
  };

  const adoptIdentifiers = (result: Ga4IdentifierPair): void => {
    const observedAt = now();
    clientIdCapturedAt = observedAt;
    sessionIdCapturedAt = observedAt;
    identifiers = {
      ...result,
      ga_session_observed_at: new Date(observedAt).toISOString(),
    };
    onChange(identifiers);
  };

  const refresh = async (): Promise<Ga4Identifiers | null> => {
    if (!targetWindow || !isCookiebotAnalyticsConsentGiven(targetWindow)) {
      clear();
      return null;
    }

    const requestGeneration = consentGeneration;
    if (pendingLookup && pendingGeneration === requestGeneration) {
      return pendingLookup;
    }

    pendingGeneration = requestGeneration;
    pendingLookup = lookup()
      .then((result) => {
        if (
          consentGeneration !== requestGeneration ||
          !isCookiebotAnalyticsConsentGiven(targetWindow)
        ) {
          return identifiers;
        }

        if (result) {
          adoptIdentifiers(result);
        }

        return identifiers;
      })
      .finally(() => {
        if (pendingGeneration === requestGeneration) {
          pendingLookup = null;
          pendingGeneration = null;
        }
      });

    return pendingLookup;
  };

  const handleConsentChange = (): void => {
    consentGeneration += 1;
    if (!isCookiebotAnalyticsConsentGiven(targetWindow)) {
      clear();
      return;
    }

    void refresh();
  };

  const start = (): void => {
    if (!targetWindow) return;
    targetWindow.addEventListener("CookiebotOnLoad", handleConsentChange);
    targetWindow.addEventListener("CookiebotOnAccept", handleConsentChange);
    targetWindow.addEventListener(
      "CookiebotOnConsentChanged",
      handleConsentChange,
    );
    targetWindow.addEventListener("CookiebotOnDecline", handleConsentChange);
    handleConsentChange();
  };

  const destroy = (): void => {
    if (!targetWindow) return;
    targetWindow.removeEventListener("CookiebotOnLoad", handleConsentChange);
    targetWindow.removeEventListener("CookiebotOnAccept", handleConsentChange);
    targetWindow.removeEventListener(
      "CookiebotOnConsentChanged",
      handleConsentChange,
    );
    targetWindow.removeEventListener("CookiebotOnDecline", handleConsentChange);
  };

  const getFreshIdentifiers = (): Ga4Identifiers | null => {
    if (!targetWindow || !isCookiebotAnalyticsConsentGiven(targetWindow)) {
      clear();
      return null;
    }

    const currentTime = now();
    const sessionAge =
      identifiers && clientIdCapturedAt !== null
        ? currentTime - (sessionIdCapturedAt ?? clientIdCapturedAt)
        : null;
    const needsCookieRecovery =
      !identifiers ||
      clientIdCapturedAt === null ||
      identifiers.ga_session_id === null ||
      sessionAge === null ||
      sessionAge < 0 ||
      sessionAge > GA4_IDENTIFIER_MAX_AGE_MS;

    // Submit-time fallback for callers that cannot await the async lookup:
    // exit beacons and order submits read the identifiers synchronously. This
    // also restores a session id after the volatile cache entry ages out.
    if (needsCookieRecovery) {
      const cookiePair = cookieLookup();

      if (cookiePair?.ga_client_id) {
        adoptIdentifiers({
          ga_client_id: cookiePair.ga_client_id,
          ga_session_id: cookiePair.ga_session_id,
        });
      }
    }

    if (!identifiers || clientIdCapturedAt === null) {
      clear();
      return null;
    }

    const clientAge = now() - clientIdCapturedAt;
    if (clientAge < 0 || clientAge > GA4_CLIENT_ID_MAX_AGE_MS) {
      clear();
      return null;
    }

    const refreshedSessionAge =
      now() - (sessionIdCapturedAt ?? clientIdCapturedAt);
    if (
      refreshedSessionAge < 0 ||
      refreshedSessionAge > GA4_IDENTIFIER_MAX_AGE_MS
    ) {
      // Only the volatile GA4 session id ages out. Dropping the client id as
      // well used to strip the stable attribution anchor from long checkout
      // journeys, which is why both fields are evaluated independently.
      if (identifiers.ga_session_id === null) {
        return identifiers;
      }

      identifiers = { ...identifiers, ga_session_id: null };
      onChange(identifiers);
    }

    return identifiers;
  };

  return { clear, destroy, getFreshIdentifiers, refresh, start };
};
